import {
  instantFromDate,
  instantIntervalFromDateRangeWithZone,
  instantIntervalFromRange,
  type DateRange,
  type WeekRange,
} from '@airreading/common';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Calendar, momentLocalizer, type Components, type EventPropGetter, type View } from 'react-big-calendar';
import { Temporal } from 'temporal-polyfill';
import type { DateLocalizer } from 'react-big-calendar';

import { useTimeZone } from '@/auth/useTimeZone';

import './calendar.css';
import { EventWrapper } from './session-schedule-calendar-event-popover';
import {
  EventContext,
  backgroundColorForCoachSessionRemarks,
  coachCalendarTimeGridSessionGapClasses,
  substitutionRequestSummariesForSessions,
} from './session-schedule-calendar-shared';
import { ToolbarWrapper } from './session-schedule-calendar-toolbar';
import type { CalendarView, ExpandedEvent, SessionScheduleProps } from './session-schedule-calendar-types';
import { SessionNextClassTooltip } from './session-next-class-tooltip';

//#region Moment localizer (DST / arbitrary display time zone)

/**
 * `react-big-calendar`'s moment localizer iterates calendar days with
 * `moment(date).add(1, 'day')`. After `moment.tz.setDefault`, that breaks on
 * DST transition days (e.g. Africa/Cairo): the next "day" can still be the same
 * calendar date, so the week view shows duplicate columns and event layout drifts.
 *
 * This wrapper keeps moment for formatting while:
 * - building `range()` day lists via `Temporal.PlainDate` (one column per civil day)
 * - fixing `add(..., 'day')` so civil-day steps always land on the next day's midnight
 * - routing `getTimezoneOffset` / `getDstOffset` through the display `timeZone`
 *   (RBC's defaults use the browser offset, which mis-positions slots vs. events)
 * - clearing `segmentOffset`, which was compensating for the wrong offset model
 */
type LocalizerRangeUnit = Parameters<DateLocalizer['range']>[2];
type LocalizerAddUnit = Parameters<DateLocalizer['add']>[2];

function createTimeZoneAwareMomentLocalizer(timeZone: string): DateLocalizer {
  moment.tz.setDefault(timeZone);
  const localizer = momentLocalizer(moment) as DateLocalizer;
  const origRange = localizer.range.bind(localizer);
  const origAdd = localizer.add.bind(localizer);

  const calendarDayMidnightDatesBetween = (start: Date, end: Date): Date[] => {
    let day = Temporal.Instant.fromEpochMilliseconds(start.getTime()).toZonedDateTimeISO(timeZone).toPlainDate();
    const lastDay = Temporal.Instant.fromEpochMilliseconds(end.getTime()).toZonedDateTimeISO(timeZone).toPlainDate();
    const days: Date[] = [];
    while (Temporal.PlainDate.compare(day, lastDay) <= 0) {
      const zdt = day.toZonedDateTime({ timeZone, plainTime: Temporal.PlainTime.from('00:00') });
      days.push(new Date(zdt.epochMilliseconds));
      day = day.add({ days: 1 });
    }
    return days;
  };

  localizer.range = (start: Date, end: Date, unit?: LocalizerRangeUnit) => {
    const u = (unit ?? 'day').toString().toLowerCase();
    if (u === 'day' || u === 'days' || u === 'd') {
      return calendarDayMidnightDatesBetween(start, end);
    }
    return origRange(start, end, unit);
  };

  localizer.add = (date: Date, amount: number, unit: LocalizerAddUnit) => {
    const u = unit.toString().toLowerCase();
    if (u === 'day' || u === 'days' || u === 'd') {
      const zonedMoment = moment.tz(date, timeZone);
      const isCalendarMidnight =
        zonedMoment.hour() === 0 &&
        zonedMoment.minute() === 0 &&
        zonedMoment.second() === 0 &&
        zonedMoment.millisecond() === 0;

      if (isCalendarMidnight) {
        return moment
          .tz(zonedMoment.format('YYYY-MM-DD'), timeZone)
          .startOf('day')
          .add(amount, 'day')
          .startOf('day')
          .toDate();
      }

      return zonedMoment.add(amount, 'day').toDate();
    }
    return origAdd(date, amount, unit);
  };

  localizer.getTimezoneOffset = (date: Date) => -moment.tz(date, timeZone).utcOffset();

  localizer.getDstOffset = (start: Date, end: Date) =>
    moment.tz(end, timeZone).utcOffset() - moment.tz(start, timeZone).utcOffset();

  const wallClockMinutes = (date: Date) => {
    const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(date.getTime()).toZonedDateTimeISO(timeZone);
    return zonedDateTime.hour * 60 + zonedDateTime.minute;
  };

  localizer.getMinutesFromMidnight = (date: Date) => wallClockMinutes(date);

  localizer.getTotalMin = (start: Date, end: Date) => {
    const startZdt = Temporal.Instant.fromEpochMilliseconds(start.getTime()).toZonedDateTimeISO(timeZone);
    const endZdt = Temporal.Instant.fromEpochMilliseconds(end.getTime()).toZonedDateTimeISO(timeZone);
    const dayDelta = startZdt.toPlainDate().until(endZdt.toPlainDate(), { largestUnit: 'day' }).days;
    return dayDelta * 24 * 60 + (wallClockMinutes(end) - wallClockMinutes(start));
  };

  localizer.segmentOffset = 0;

  return localizer;
}

//#endregion

//#region Main definition

export function SessionScheduleCalendarLegacy({
  isLoading = false,
  events,
  sessionById,
  activeInstant,
  setActiveInstant,
  onRangeChange,
  views,
  view,
  onView,
  toolbarLeftAddon,
}: SessionScheduleProps) {
  const { timeZone } = useTimeZone();

  const localizer = useMemo(() => createTimeZoneAwareMomentLocalizer(timeZone), [timeZone]);

  const activeDate = useMemo(() => new Date(activeInstant.epochMilliseconds), [activeInstant]);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const clearActiveEvent = useCallback(() => setActiveEventId(null), [setActiveEventId]);
  const substitutionRequestSummaryBySessionId = useMemo(
    () => substitutionRequestSummariesForSessions(sessionById),
    [sessionById],
  );
  const eventContext = useMemo(
    () => ({
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon: toolbarLeftAddon ?? null,
    }),
    [
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon,
    ],
  );

  const didNavigate = useCallback(
    (newDate: Date) => {
      setActiveInstant(instantFromDate(newDate));
    },
    [setActiveInstant],
  );
  const didSelectEvent = useCallback(
    (eventLike: ExpandedEvent) => {
      const { id } = eventLike;
      setActiveEventId(id);
    },
    [setActiveEventId],
  );
  const didChangeRange = useCallback(
    (range: WeekRange | DateRange) => {
      const instantInterval =
        'end' in range
          ? instantIntervalFromDateRangeWithZone(range, timeZone)
          : instantIntervalFromRange(range, timeZone);
      onRangeChange?.(instantInterval);
    },
    [timeZone, onRangeChange],
  );

  const didDrillDown = useCallback(
    (newDate: Date) => {
      setActiveInstant(instantFromDate(newDate));
      // Then change the view after a small delay to ensure state updates are processed
      setTimeout(() => {
        onView('day');
      }, 0);
    },
    [onView, setActiveInstant],
  );

  const eventPropGetter = useCallback<EventPropGetter<ExpandedEvent>>(
    (event) => {
      const isPast = event.end ? event.end < new Date() : false;
      const className = clsx(
        '!outline-none px-[8px]',
        view === 'month' ? '!border-none py-[2px]' : coachCalendarTimeGridSessionGapClasses,
        view === 'day' ? 'py-[4px]' : view === 'week' ? 'py-[8px]' : null,
        isPast ? 'bg-air-light-grey' : backgroundColorForCoachSessionRemarks(event.coachSessionRemarksV2),
      );
      return { className };
    },
    [view],
  );

  const handleViewChange = useCallback(
    (newView: View) => {
      onView(newView as CalendarView);
    },
    [onView],
  );

  return (
    <EventContext.Provider value={eventContext}>
      <Calendar
        localizer={localizer}
        defaultView="week"
        views={views as View[]}
        view={view as View}
        onView={handleViewChange}
        date={activeDate}
        onNavigate={didNavigate}
        onSelectEvent={didSelectEvent}
        onRangeChange={didChangeRange}
        onDrillDown={didDrillDown}
        doShowMoreDrillDown={true}
        events={events}
        step={15}
        formats={{ eventTimeRangeFormat: emptyEventTimeRangeFormat }}
        components={customComponents}
        eventPropGetter={eventPropGetter}
        showMultiDayTimes={true}
        scrollToTime={
          new Date(
            Temporal.Instant.from(activeInstant.toString())
              .subtract({ minutes: 15 })
              .toZonedDateTimeISO(timeZone)
              .toInstant().epochMilliseconds,
          )
        }
      />
      <SessionNextClassTooltip events={events} timeZone={timeZone} />
    </EventContext.Provider>
  );
}

//#endregion

//#region UI customization

function emptyEventTimeRangeFormat() {
  return '';
}

const customComponents = {
  event: EventWrapper,
  toolbar: ToolbarWrapper,
  daySlotWrapper: DaySlotWrapper,
  timeSlotWrapper: TimeSlotWrapper as React.ComponentType,
  header: HeaderWrapper,
} as Components<ExpandedEvent> & {
  daySlotWrapper: React.ComponentType<{ children: ReactNode; value: Date }>;
  header: React.ComponentType<{ date: Date; label: string }>;
};

function DaySlotWrapper({ children, value }: { children: ReactNode; value: Date }) {
  const dateStr = value.toISOString().split('T')[0]; // YYYY-MM-DD format
  return (
    <div data-testid={`sessions-calendar-day-slot-${dateStr}`} className="rbc-day-slot">
      {children}
    </div>
  );
}

function TimeSlotWrapper({ children, value }: { children: ReactNode; value: Date }) {
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const timeStr = `${hours.toString().padStart(2, '0')}-${minutes.toString().padStart(2, '0')}`;
  return (
    <div data-testid={`sessions-calendar-time-slot-${timeStr}`} className="rbc-time-slot">
      {children}
    </div>
  );
}

function HeaderWrapper({ date, label }: { date: Date; label: string }) {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
  return (
    <div data-testid={`sessions-calendar-header-${dateStr}`} className="rbc-header">
      {label}
    </div>
  );
}

//#endregion
